import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, Modal, Space, Switch, Tag } from "antd";
import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import Table from "../../../core/common/dataTable/index";
import Toasts from "../../../core/common/toast";
import {
  createSMSTemplate,
  deleteSMSTemplate,
  getSMSTemplates,
  resetAll,
} from "../../../core/data/redux/smsTemplatesSlice";
import { all_routes } from "../../router/all_routes";

import { isSupportViewer } from "../../../utils/permissionUtils";

const routes = all_routes;

interface SMSTemplateData {
  id: string;
  sms_title: string;
  msg: string;
  status: boolean;
  templateId?: string;
  created_at?: string;
  updated_at?: string;
}

const SMSTemplates: React.FC = () => {
  const dispatch = useDispatch();
  const smsTemplatesState = useSelector(
    (state: any) => state.smsTemplatesState
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [viewingTemplate, setViewingTemplate] =
    useState<SMSTemplateData | null>(null);
  const [showUpdateToast, setShowUpdateToast] = useState(false);
  const [showDeleteToast, setShowDeleteToast] = useState(false);
  const [showDeleteErrorToast, setShowDeleteErrorToast] = useState(false);
  const [showFormErrorToast, setShowFormErrorToast] = useState(false);
  const [showCreateSuccessToast, setShowCreateSuccessToast] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");
  const [deleteMessage, setDeleteMessage] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [createSuccessMessage, setCreateSuccessMessage] = useState("");
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const templatesData = smsTemplatesState?.templatesData || null;
  const createData = smsTemplatesState?.createData || null;
  const deleteData = smsTemplatesState?.deleteData || null;

  const fetchData = (page = pagination.current, limit = pagination.pageSize, query = searchText) => {
    dispatch(
      getSMSTemplates({
        page,
        limit,
        query,
      })
    );
  };

  const debouncedFetch = useCallback(
    debounce((p: any, q: string) => {
      fetchData(p.current, p.pageSize, q);
    }, 500),
    []
  );

  const handleSearch = (value: string) => {
    setSearchText(value);
    const resetPagination = { ...pagination, current: 1 };
    setPagination(resetPagination);

    if (value === "") {
      debouncedFetch.cancel();
      fetchData(1, pagination.pageSize, "");
    } else {
      debouncedFetch(resetPagination, value);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.pageSize]);

  useEffect(() => {
    if (
      smsTemplatesState.action === "create" &&
      smsTemplatesState.loading === "fulfilled"
    ) {
      setCreateSuccessMessage(
        createData?.message || "SMS template created/updated successfully!"
      );
      setShowCreateSuccessToast(true);
      setTimeout(() => {
        setShowCreateSuccessToast(false);
        dispatch(resetAll());
      }, 4000);
      setIsModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchData();
    }
  }, [smsTemplatesState.action, smsTemplatesState.loading, createData]);

  useEffect(() => {
    if (
      smsTemplatesState.action === "delete" &&
      smsTemplatesState.loading === "fulfilled"
    ) {
      setDeleteMessage(
        deleteData?.message || "SMS template deleted successfully!"
      );
      setShowDeleteToast(true);
      setTimeout(() => {
        setShowDeleteToast(false);
        dispatch(resetAll());
      }, 3000);
      fetchData(); // Refresh the list
    }
  }, [smsTemplatesState.action, smsTemplatesState.loading, deleteData]);

  useEffect(() => {
    if (
      smsTemplatesState.action === "delete" &&
      smsTemplatesState.loading === "rejected"
    ) {
      setDeleteMessage(
        smsTemplatesState.error || "Failed to delete SMS template!"
      );
      setShowDeleteErrorToast(true);
      setTimeout(() => {
        setShowDeleteErrorToast(false);
        dispatch(resetAll());
      }, 3000);
    }
  }, [
    smsTemplatesState.action,
    smsTemplatesState.loading,
    smsTemplatesState.error,
  ]);

  useEffect(() => {
    try {
      if (templatesData) {
        let total = 0;
        let currentPage = pagination.current;
        let pageSize = pagination.pageSize;

        // Try different response structures for pagination info
        if (templatesData.data?.total !== undefined) {
          total = templatesData.data.total;
          currentPage = templatesData.data.page || pagination.current;
          pageSize = templatesData.data.limit || pagination.pageSize;
        } else if (templatesData.data?.data?.total !== undefined) {
          total = templatesData.data.data.total;
          currentPage = templatesData.data.data.page || pagination.current;
          pageSize = templatesData.data.data.limit || pagination.pageSize;
        } else {
          // Fallback: count array length
          const dataArray = getTableData();
          total = dataArray.length;
        }

        setPagination((prev) => ({
          current: currentPage,
          pageSize: pageSize,
          total: total,
        }));
      }
    } catch (error) {
      // Handle pagination error silently
    }
  }, [templatesData]);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (_: any, __: any, index: number) => (
        <div>{(pagination.current - 1) * pagination.pageSize + index + 1}</div>
      ),
      width: 80,
    },
    {
      title: "SMS Title",
      dataIndex: "sms_title",
      render: (title: string) => (
        <div style={{ fontWeight: 500, color: "#1890ff" }}>{title || "-"}</div>
      ),
      sorter: (a: SMSTemplateData, b: SMSTemplateData) =>
        (a.sms_title || "").localeCompare(b.sms_title || ""),
    },
    {
      title: "Template ID",
      dataIndex: "templateId",
      render: (templateId: string) => (
        <div
          style={{
            fontFamily: "monospace",
            fontSize: "12px",
            backgroundColor: "#f0f0f0",
            padding: "2px 6px",
            borderRadius: "3px",
            display: "inline-block",
          }}
        >
          {templateId || "Not Set"}
        </div>
      ),
      width: 140,
    },
    {
      title: "Message Preview",
      dataIndex: "msg",
      render: (msg: string) => (
        <div
          style={{
            maxWidth: "250px",
            wordWrap: "break-word",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: "1.4",
          }}
        >
          {msg || "-"}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: boolean) => (
        <Tag color={status ? "success" : "error"} style={{ fontWeight: 500 }}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
      width: 100,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date: string) => (
        <div style={{ fontSize: "12px" }}>
          {date
            ? new Date(date).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            : "N/A"}
        </div>
      ),
      width: 120,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: any, record: SMSTemplateData) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
            title="View Template"
          />
          {!isSupportViewer() && (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                size="small"
                title="Edit Template"
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
                size="small"
                title="Delete Template"
              />
            </>
          )}
        </Space>
      ),
      width: 120,
    },
  ];

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      status: true,
    });
    setIsModalVisible(true);
  };

  const handleEdit = (record: SMSTemplateData) => {
    setEditingId(record.id);
    form.setFieldsValue({
      sms_title: record.sms_title,
      msg: record.msg,
      status: record.status,
      templateId: record.templateId,
    });
    setIsModalVisible(true);
  };

  const handleView = (record: SMSTemplateData) => {
    setViewingTemplate(record);
    setIsViewModalVisible(true);
  };

  const handleDelete = (id: string | number) => {
    if (!id) {
      return;
    }
    setDeletingId(String(id));
    setIsDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    if (deletingId) {
      dispatch(deleteSMSTemplate({ id: deletingId }));
      setIsDeleteModalVisible(false);
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalVisible(false);
    setDeletingId(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        ...(editingId && { id: editingId }),
        sms_title: values.sms_title,
        msg: values.msg,
        status: values.status !== undefined ? values.status : true,
        templateId: values.templateId,
      };

      dispatch(createSMSTemplate(payload));
    } catch (error) {
      setFormErrorMessage("Please check all required fields and try again.");
      setShowFormErrorToast(true);
      setTimeout(() => setShowFormErrorToast(false), 3000);
    }
  };

  const handlePagination = (paginationConfig: any) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
    fetchData(paginationConfig.current, paginationConfig.pageSize, searchText);
  };

  const getTableData = () => {
    // Try multiple possible response structures
    if (
      templatesData?.data?.data?.data &&
      Array.isArray(templatesData.data.data.data)
    ) {
      return templatesData.data.data.data;
    } else if (
      templatesData?.data?.data &&
      Array.isArray(templatesData.data.data)
    ) {
      return templatesData.data.data;
    } else if (templatesData?.data && Array.isArray(templatesData.data)) {
      return templatesData.data;
    } else if (templatesData?.result && Array.isArray(templatesData.result)) {
      return templatesData.result;
    } else if (Array.isArray(templatesData)) {
      return templatesData;
    }

    return [];
  };

  const tableData = getTableData();

  return (
    <div className="page-wrapper">
      <Toasts
        show={smsTemplatesState.loading === "rejected"}
        message={smsTemplatesState.error}
        title={"Failed"}
        type={"danger"}
      />
      <Toasts
        show={showCreateSuccessToast}
        message={createSuccessMessage}
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={showDeleteToast}
        message={deleteMessage}
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={showDeleteErrorToast}
        message={deleteMessage}
        title={"Error"}
        type={"danger"}
      />
      <Toasts
        show={showFormErrorToast}
        message={formErrorMessage}
        title={"Validation Error"}
        type={"danger"}
      />

      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">SMS Templates</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Templates</li>
                <li className="breadcrumb-item active" aria-current="page">
                  SMS Templates
                </li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2 d-flex align-items-center gap-2">
            {!isSupportViewer() && (
              <Button
                type="primary"
                onClick={handleAdd}
                icon={<PlusOutlined />}
                style={{
                  backgroundColor: "#c0282c",
                  border: "1px solid #c0282c",
                  fontWeight: "500",
                }}
              >
                Add SMS Template
              </Button>
            )}

            <CollapseHeader />
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
            <h5 className="card-title mb-0">
              <i className="ti ti-message me-2" />
              SMS Templates ({pagination.total} total)
            </h5>
          </div>
          <div className="card-body p-0 py-3">
            {smsTemplatesState.loading === "rejected" && (
              <div style={{ textAlign: "center", padding: "20px", color: "#ff4d4f" }}>
                <i className="ti ti-alert-circle" style={{ fontSize: "24px", marginRight: "8px" }} />
                <span>Failed to load SMS templates: {smsTemplatesState.error}</span>
                <Button type="link" onClick={() => fetchData()}>Try Again</Button>
              </div>
            )}
            <Table
              columns={columns}
              dataSource={tableData}
              Selection={false}
              paginationConfig={pagination}
              handlePagination={handlePagination}
              loading={smsTemplatesState.loading === "pending"}
              handleSearch={handleSearch}
              searchValue={searchText}
            />
          </div>
        </div>

        {/* Create/Edit Modal */}
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i className={`ti ti-${editingId ? "edit" : "plus"}`} />
              {editingId ? "Edit SMS Template" : "Add SMS Template"}
            </div>
          }
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
            setEditingId(null);
          }}
          confirmLoading={
            smsTemplatesState.loading === "pending" &&
            smsTemplatesState.action === "create"
          }
          width={600}
          okText={editingId ? "Update" : "Create"}
          cancelText="Cancel"
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="sms_title"
              label="SMS Title"
              rules={[
                { required: true, message: "Please input SMS title!" },
                { min: 2, message: "SMS title must be at least 2 characters!" },
                {
                  max: 100,
                  message: "SMS title cannot exceed 100 characters!",
                },
              ]}
            >
              <Input placeholder="Enter SMS template title" />
            </Form.Item>

            <Form.Item
              name="templateId"
              label="Template ID"
              rules={[
                { required: true, message: "Please input template ID!" },
                {
                  min: 2,
                  message: "Template ID must be at least 2 characters!",
                },
                {
                  max: 50,
                  message: "Template ID cannot exceed 50 characters!",
                },
                {
                  pattern: /^[a-zA-Z0-9_-]+$/,
                  message: "Template ID can only contain letters, numbers, underscores, and hyphens!",
                },
              ]}
            >
              <Input placeholder="Enter unique template identifier (e.g., otp_sms, welcome_sms)" />
            </Form.Item>

            <Form.Item
              name="msg"
              label="SMS Message"
              rules={[
                { required: true, message: "Please input message!" },
                { min: 10, message: "Message must be at least 10 characters!" },
                {
                  max: 160,
                  message:
                    "SMS message should not exceed 160 characters for optimal delivery!",
                },
              ]}
            >
              <Input.TextArea
                placeholder="Enter SMS message content (plain text only, max 160 characters recommended)"
                rows={4}
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item name="status" label="Status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Form>
        </Modal>

        {/* View Modal */}
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="ti ti-eye" />
              View SMS Template
            </div>
          }
          open={isViewModalVisible}
          onCancel={() => {
            setIsViewModalVisible(false);
            setViewingTemplate(null);
          }}
          footer={[
            <Button key="close" onClick={() => setIsViewModalVisible(false)}>
              Close
            </Button>,
          ]}
          width={600}
        >
          {viewingTemplate && (
            <div style={{ padding: "16px 0" }}>
              <div style={{ marginBottom: "16px" }}>
                <strong>Title:</strong>
                <div
                  style={{
                    marginTop: "8px",
                    padding: "8px 12px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  {viewingTemplate?.sms_title || 'N/A'}
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <strong>Template ID:</strong>
                <div
                  style={{
                    marginTop: "8px",
                    padding: "8px 12px",
                    backgroundColor: "#f0f0f0",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                  }}
                >
                  {viewingTemplate?.templateId || 'N/A'}
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <strong>SMS Message:</strong>
                <div
                  style={{
                    marginTop: "8px",
                    padding: "12px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                    minHeight: "80px",
                    whiteSpace: "pre-wrap",
                    fontFamily: "inherit",
                  }}
                >
                  {viewingTemplate?.msg || 'N/A'}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <strong>Status:</strong>
                  <Tag
                    color={viewingTemplate?.status ? "success" : "error"}
                    style={{ marginLeft: "8px" }}
                  >
                    {viewingTemplate?.status ? "Active" : "Inactive"}
                  </Tag>
                </div>

                {viewingTemplate?.created_at && (
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    Created:{" "}
                    {new Date(viewingTemplate.created_at).toLocaleDateString(
                      "en-IN"
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i
                className="ti ti-alert-triangle"
                style={{ color: "#ff4d4f" }}
              />
              Confirm Delete
            </div>
          }
          open={isDeleteModalVisible}
          onOk={confirmDelete}
          onCancel={cancelDelete}
          confirmLoading={
            smsTemplatesState.loading === "pending" &&
            smsTemplatesState.action === "delete"
          }
          okText="Yes, Delete"
          cancelText="Cancel"
          okType="danger"
          centered
        >
          <p>Are you sure you want to delete this SMS template?</p>
          <p style={{ color: "#666", fontSize: "14px" }}>
            This action cannot be undone.
          </p>
        </Modal>
      </div>

      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">DMRC Admin Panel, All Rights Reserved.</p>
      </div>
    </div>
  );
};

export default SMSTemplates;
